import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { userService } from "../services/api";
import { useLanguage } from "../context/LanguageContext";
// import { useTheme as useCustomTheme } from "../context/ThemeContext"; // Not needed if using MUI theme directly
import { toast } from "react-toastify"; // Keep for notifications

// Material UI components
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid,
  IconButton, InputAdornment, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography, Chip, Stack, Alert, AlertTitle, useTheme, InputLabel, Hidden,
  TableSortLabel, Tooltip
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// Material UI icons
import {
  AddCircleOutline as AddIcon, // Outlined
  Search as SearchIcon, ClearOutlined as ClearIcon, // Outlined
  EditOutlined as EditIcon, // Outlined
  DeleteOutline as DeleteIcon, // Outlined
  PersonOutline as PersonIcon, // Outlined
  AdminPanelSettingsOutlined as AdminIcon, // Outlined
  EngineeringOutlined as ManagerIcon, // Changed from EngineerIcon for clarity
  BuildOutlined as OperatorIcon, // Outlined
  RefreshOutlined as RefreshIcon, // Outlined
  SupervisorAccountOutlined as PageIcon, // For page title
  UnfoldMore as UnfoldMoreIcon,
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";
import ContentSection from "../components/ContentSection";
import moment from "moment";

const UserManagement = () => {
  const { currentUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const theme = useTheme(); // MUI Theme

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    searchTerm: "",
    role: "",
  });

  const [modalMode, setModalMode] = useState("create");
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "operator" });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [pagination, setPagination] = useState({ currentPage: 0, pageSize: 10, totalItems: 0 });
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {
        limit: pagination.pageSize, page: pagination.currentPage + 1, sortBy, sortOrder: sortDirection,
        search: filters.searchTerm || undefined, role: filters.role || undefined,
      };
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await userService.getAll(params);
      const data = response.data?.users || response.data?.data || response.data || [];
      setUsers(Array.isArray(data) ? data : []);
      setPagination(prev => ({ ...prev, totalItems: response.data?.totalUsers || response.data?.pagination?.total || data.length || 0 }));
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(t("errorFetchingUsers") + (err.message ? `: ${err.message}` : ''));
    } finally { setLoading(false); }
  }, [pagination.pageSize, pagination.currentPage, sortBy, sortDirection, filters, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc"); setSortBy(field);
    setPagination(prev => ({ ...prev, currentPage: 0 })); // Reset page on sort
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, currentPage: 0 })); // Reset page on filter
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({ ...prev, [name]: value }));
    setFormError(null); // Clear form error when user types
  };

  const resetFilters = () => {
    setFilters({ searchTerm: "", role: "" });
    setSortBy("name"); setSortDirection("asc");
    setPagination(prev => ({ ...prev, currentPage: 0 }));
    // fetchUsers will be triggered by state changes
  };


  const handleCloseModal = () => {
    setShowUserModal(false); setSelectedUser(null);
    setUserForm({ name: "", email: "", password: "", role: "operator" });
    setFormError(null);
  };

  const handleShowCreateModal = () => { setModalMode("create"); handleCloseModal(); setShowUserModal(true); };
  const handleShowEditModal = (user) => {
    setModalMode("edit"); setSelectedUser(user);
    setUserForm({ name: user.name, email: user.email, password: "", role: user.role });
    setShowUserModal(true);
  };

  const validateUserForm = () => {
    setFormError(null);
    if (!userForm.name.trim()) { setFormError(t("nameIsRequired")); return false; }
    if (!userForm.email.trim()) { setFormError(t("emailIsRequired")); return false; }
    if (!/\S+@\S+\.\S+/.test(userForm.email)) { setFormError(t("invalidEmailFormat")); return false; }
    if (modalMode === "create" && !userForm.password) { setFormError(t("passwordIsRequiredForNewUser")); return false; }
    if (userForm.password && userForm.password.length < 6) { setFormError(t("passwordMinLength", { length: 6 })); return false; }
    return true;
  };

  const handleUserFormSubmit = async () => {
    if (!validateUserForm()) return;
    setFormSubmitting(true); setFormError(null);
    try {
      const userData = { ...userForm };
      if (modalMode === 'edit' && !userData.password) delete userData.password; // Don't send empty password for update

      if (modalMode === "create") {
        await userService.create(userData);
        toast.success(t("userCreatedSuccessfully"));
      } else {
        await userService.update(selectedUser._id, userData);
        toast.success(t("userUpdatedSuccessfully"));
      }
      fetchUsers(); // Refresh list
      handleCloseModal();
    } catch (err) {
      console.error("Error saving user:", err);
      const errMsg = err.response?.data?.message || t("errorSavingUser");
      setFormError(errMsg); toast.error(errMsg);
    } finally { setFormSubmitting(false); }
  };

  const handleDeleteClick = (user) => { setUserToDelete(user); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setUserToDelete(null); };
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      await userService.delete(userToDelete._id);
      toast.success(t("userDeletedSuccessfully"));
      fetchUsers(); // Refresh list
      handleCloseDeleteModal();
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error(err.response?.data?.message || t("errorDeletingUser"));
    } finally { setDeleteLoading(false); }
  };

  const handleChangePage = (event, newPage) => setPagination(prev => ({ ...prev, currentPage: newPage }));
  const handleChangeRowsPerPage = (event) => setPagination(prev => ({ ...prev, pageSize: parseInt(event.target.value, 10), currentPage: 0 }));

  const getRoleChipProps = (role) => {
    switch (role) {
      case "admin": return { color: "error", icon: <AdminIcon fontSize="inherit" />, label: t('admin') };
      case "manager": return { color: "primary", icon: <ManagerIcon fontSize="inherit" />, label: t('manager') };
      case "operator": return { color: "success", icon: <OperatorIcon fontSize="inherit" />, label: t('operator') };
      default: return { color: "default", icon: <PersonIcon fontSize="inherit" />, label: t(role) };
    }
  };

  if (loading && users.length === 0) {
    return <PageContainer title={t("userManagement")}><Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box></PageContainer>;
  }

  return (
    <PageContainer title={t("userManagement")}>
      <ContentSection title={t("manageUsers")} icon={<PageIcon color="primary" />} elevation={0} sx={{ p: 0 }}
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={handleShowCreateModal}>{t("addNewUser")}</Button>}>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}><AlertTitle>{t("error")}</AlertTitle>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 2.5, borderRadius: theme.shape.borderRadius }} variant="outlined">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={5}>
              <TextField fullWidth name="searchTerm" label={t("searchUsers")} variant="outlined" size="small" value={filters.searchTerm} onChange={handleFilterChange}
                InputProps={{ endAdornment: filters.searchTerm && <InputAdornment position="end"><IconButton size="small" onClick={() => { setFilters(prev => ({ ...prev, searchTerm: '' })); }}><ClearIcon fontSize="small" /></IconButton></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small"> <InputLabel>{t("role")}</InputLabel>
                <Select name="role" value={filters.role} label={t("role")} onChange={handleFilterChange}>
                  <MenuItem value="">{t("allRoles")}</MenuItem> <MenuItem value="admin">{t("admin")}</MenuItem> <MenuItem value="manager">{t("manager")}</MenuItem> <MenuItem value="operator">{t("operator")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} md={4} container justifyContent="flex-end" spacing={1}>
              <Grid item><Button variant="text" onClick={resetFilters} size="medium" startIcon={<RefreshIcon />}>{t("resetFilters")}</Button></Grid>
            </Grid>
          </Grid>
        </Paper>

        {loading && users.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : users.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 50, color: 'text.disabled' }} />
            <Typography variant="h6" gutterBottom>{filters.searchTerm || filters.role ? t("noMatchingUsers") : t("noUsersFound")}</Typography>
            <Button variant="contained" onClick={handleShowCreateModal} startIcon={<AddIcon />}>{t("addNewUser")}</Button>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[{ id: 'name', label: t('name') }, { id: 'email', label: t('email') }, { id: 'role', label: t('role') }, { id: 'lastLogin', label: t('lastLogin') }, { id: 'createdAt', label: t('createdAt') }].map(col => (
                    <TableCell key={col.id} sortDirection={sortBy === col.id ? sortDirection : false}>
                      <TableSortLabel active={sortBy === col.id} direction={sortBy === col.id ? sortDirection : 'asc'} onClick={() => handleSort(col.id)} IconComponent={UnfoldMoreIcon}>{col.label}</TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right">{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow hover key={user._id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell><Typography variant="body2" fontWeight="medium">{user.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                    <TableCell><Chip size="small" {...getRoleChipProps(user.role)} /></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{user.lastLogin ? moment(user.lastLogin).fromNow() : t("never")}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{moment(user.createdAt).format("L")}</Typography></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t("editUser")}><IconButton size="small" onClick={() => handleShowEditModal(user)} color="primary"><EditIcon /></IconButton></Tooltip>
                        <Tooltip title={t("deleteUser")}><IconButton size="small" onClick={() => handleDeleteClick(user)} color="error" disabled={user._id === currentUser.id || user.role === "admin"}><DeleteIcon /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={pagination.totalItems} rowsPerPage={pagination.pageSize} page={pagination.currentPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage={t("rowsPerPage")} labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("of")} ${count}`} />
          </TableContainer>
        )}
      </ContentSection>

      <Dialog open={showUserModal} onClose={handleCloseModal} fullWidth maxWidth="xs">
        <DialogTitle>{modalMode === "create" ? t("createNewUser") : t("editUser")}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>}
          <TextField margin="dense" label={t("name")} name="name" fullWidth variant="outlined" value={userForm.name} onChange={handleInputChange} error={!!formError && formError.toLowerCase().includes('name')} autoFocus />
          <TextField margin="dense" label={t("email")} name="email" type="email" fullWidth variant="outlined" value={userForm.email} onChange={handleInputChange} error={!!formError && formError.toLowerCase().includes('email')} />
          <TextField margin="dense" label={modalMode === "create" ? t("password") : t("newPasswordOptional")} name="password" type="password" fullWidth variant="outlined" value={userForm.password} onChange={handleInputChange} helperText={modalMode === 'edit' ? t('leaveBlankNoChange') : ''} error={!!formError && formError.toLowerCase().includes('password')} />
          <FormControl fullWidth margin="dense">
            <InputLabel>{t("role")}</InputLabel>
            <Select name="role" value={userForm.role} label={t("role")} onChange={handleInputChange}>
              <MenuItem value="operator">{t("operator")}</MenuItem> <MenuItem value="manager">{t("manager")}</MenuItem> <MenuItem value="admin">{t("admin")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={handleCloseModal} color="secondary">{t("cancel")}</Button>
          <Button onClick={handleUserFormSubmit} variant="contained" disabled={formSubmitting} startIcon={formSubmitting ? <CircularProgress size={18} color="inherit" /> : (modalMode === 'create' ? <AddIcon /> : <EditIcon />)}>
            {modalMode === "create" ? t("createUser") : t("saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDeleteModal} onClose={handleCloseDeleteModal} maxWidth="xs">
        <DialogTitle>{t("confirmUserDeletion")}</DialogTitle>
        <DialogContent><DialogContentText>{t("areYouSureDeleteUser", { name: userToDelete?.name || 'N/A' })} {t("thisActionCannotBeUndone")}</DialogContentText></DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={handleCloseDeleteModal} color="secondary">{t("cancel")}</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error" disabled={deleteLoading} startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}>{t("delete")}</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default UserManagement;
